import DOMPurify from "dompurify";

// Whitelist of HTML tags and attributes allowed in user-authored league descriptions.
export const ALLOWED_TAGS = [
  "p", "br", "hr",
  "h2", "h3", "h4",
  "strong", "b", "em", "i", "u", "s", "small", "mark",
  "ul", "ol", "li",
  "blockquote", "code", "pre",
  "a", "img",
  "table", "thead", "tbody", "tr", "th", "td",
  "span", "div",
];

export const ALLOWED_ATTR = ["href", "title", "target", "rel", "src", "alt"];

export interface SanitizeResult {
  clean: string;
  removedTags: string[];
  removedAttrs: string[];
}

/**
 * Sanitize user-supplied HTML against the league description whitelist.
 * Collects names of stripped tags/attributes so the UI can surface a clear message.
 */
export function sanitizeLeagueHtml(input: string): SanitizeResult {
  const removedTags = new Set<string>();
  const removedAttrs = new Set<string>();

  const onElement = (node: any) => {
    const name = (node?.nodeName || "").toLowerCase();
    if (!name) return;
    if (!ALLOWED_TAGS.includes(name) && name !== "body" && name !== "#text") {
      removedTags.add(name);
    }
  };
  const onAttribute = (_node: any, data: any) => {
    if (data && data.keepAttr === false && data.attrName) {
      removedAttrs.add(String(data.attrName).toLowerCase());
    }
  };

  DOMPurify.addHook("uponSanitizeElement", onElement);
  DOMPurify.addHook("uponSanitizeAttribute", onAttribute);

  let clean = "";
  try {
    clean = DOMPurify.sanitize(input, {
      ALLOWED_TAGS,
      ALLOWED_ATTR,
      ALLOW_DATA_ATTR: false,
      FORBID_TAGS: ["script", "style", "iframe", "object", "embed", "form", "input", "button"],
      FORBID_ATTR: ["style", "onerror", "onload", "onclick"],
      // Allow http(s), mailto, tel, and data:image/{png,jpeg,gif,webp} URIs.
      ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel):|data:image\/(?:png|jpe?g|gif|webp);base64,|#|\/|\.\/|\.\.\/)/i,
    }) as unknown as string;
  } finally {
    DOMPurify.removeHook("uponSanitizeElement");
    DOMPurify.removeHook("uponSanitizeAttribute");
  }

  // Force safe link attributes on any surviving <a>.
  clean = clean.replace(
    /<a\b([^>]*)>/gi,
    (_m, attrs) => {
      let a = attrs as string;
      if (!/\btarget=/i.test(a)) a += ' target="_blank"';
      if (!/\brel=/i.test(a)) a += ' rel="noopener noreferrer nofollow"';
      return `<a${a}>`;
    },
  );

  return {
    clean,
    removedTags: Array.from(removedTags),
    removedAttrs: Array.from(removedAttrs),
  };
}
